import { FC, useMemo, useState } from "react";
import { ScrollView } from "react-native";

import {
  Box,
  DropdownPicker,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";

import ConfigurationPanel from "./ConfigurationPanel";
import {
  AGE_BANDS,
  applyFilters,
  DEFAULT_FILTERS,
  describeFilters,
  filteredSampleSize,
  REGIONS,
  statesInRegion,
  URBANICITIES,
} from "./filters";
import { insightSummary } from "./findings";
import FindingsTab from "./FindingsTab";
import InsightCard from "./InsightCard";
import KpiPanel from "./KpiPanel";
import MapPanel from "./MapPanel";
import { AUDIENCES, optimize, predict } from "./model";
import { stateResults } from "./regions";
import ResponsesSheet, { EvidenceRequest } from "./ResponsesSheet";
import SurveyResultsTab from "./SurveyResultsTab";
import {
  AgeBand,
  AudienceId,
  Configuration,
  FactorId,
  Filters,
  KpiId,
  Region,
  Urbanicity,
} from "./types";

/* ── Types ─────────────────────────────────────── */

type Tab = "findings" | "questionnaire" | "results";

/* ── Constants ─────────────────────────────────── */

const DISCLAIMER =
  "Illustrative simulation on synthetic data. An actual pilot would incorporate Ferrero consumer, sales and distribution data.";

const TABS: { id: Tab; label: string }[] = [
  { id: "results", label: "Results" },
  { id: "findings", label: "Findings & next steps" },
  { id: "questionnaire", label: "Questionnaire" },
];

/* ── Component ─────────────────────────────────── */

const LaunchSimulator: FC = () => {
  const [audience, setAudience] = useState<AudienceId>("all");
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [selectedKpi, setSelectedKpi] = useState<KpiId>("purchaseIntent");
  const [tab, setTab] = useState<Tab>("results");
  const [evidence, setEvidence] = useState<EvidenceRequest | null>(null);
  const [edited, setEdited] = useState<Configuration | null>(null);

  // The dashboard opens on the winning scenario for the selected audience;
  // the user can then edit any knob to test alternatives.
  const optimum = useMemo(() => optimize(audience), [audience]);
  const configuration = edited ?? optimum.configuration;
  const modified = edited !== null;

  const audienceKpis = useMemo(
    () => predict(configuration, audience),
    [audience, configuration]
  );
  const kpis = useMemo(
    () => applyFilters(audienceKpis, configuration, filters),
    [audienceKpis, configuration, filters]
  );
  const national = useMemo(
    () => predict(configuration, "all"),
    [configuration]
  );
  const regional = useMemo(
    () => stateResults(configuration, kpis, audience),
    [audience, configuration, kpis]
  );
  const included = useMemo(
    () => statesInRegion(filters.region),
    [filters.region]
  );
  const insight = useMemo(
    () => insightSummary(configuration, audience),
    [audience, configuration]
  );
  const audienceLabel =
    AUDIENCES.find((a) => a.id === audience)?.label ?? "All US consumers";
  const filterLabel = describeFilters(filters);
  const sampleSize = filteredSampleSize(filters);

  const selectAudience = (next: AudienceId) => {
    setAudience(next);
    setEdited(null);
  };
  const changeFactor = (factor: FactorId, value: string) =>
    setEdited({ ...configuration, [factor]: value });

  // Tabs and filters stay put; only the tab body scrolls. The questionnaire
  // tab manages its own scrolling so its question menu can stay in view.
  return (
    <VStack className="flex-1 min-h-0 gap-6">
      <HStack className="flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
        <HStack
          className="self-start p-1 rounded-lg"
          style={{ backgroundColor: "hsl(220,13%,91%)" }}
        >
          {TABS.map((t) => (
            <Pressable
              className={`px-4 py-2 rounded-md ${tab === t.id ? "bg-background-0 shadow-soft-1" : ""}`}
              key={t.id}
              onPress={() => setTab(t.id)}
            >
              <Text
                className={`text-sm font-medium ${tab === t.id ? "text-typography-900" : "text-typography-500"}`}
              >
                {t.label}
              </Text>
            </Pressable>
          ))}
        </HStack>

        <Box className="hidden lg:flex w-px h-6 bg-outline-200" />

        <HStack className="flex-wrap items-center gap-2">
          <Text className="text-xs font-medium uppercase tracking-wide text-typography-500 mr-1">
            Audience
          </Text>
          <Box className="w-64">
            <DropdownPicker
              items={AUDIENCES.map((a) => ({
                label: a.label,
                value: a.id,
              }))}
              onChange={(value) => selectAudience(value as AudienceId)}
              placeholder="Audience"
              size="sm"
              value={audience}
            />
          </Box>
          <Box className="w-36">
            <DropdownPicker
              items={REGIONS}
              onChange={(value) =>
                setFilters((f) => ({ ...f, region: value as Region }))
              }
              placeholder="Region"
              selectKey={`region-${filters.region}`}
              size="sm"
              value={filters.region}
            />
          </Box>
          <Box className="w-32">
            <DropdownPicker
              items={AGE_BANDS}
              onChange={(value) =>
                setFilters((f) => ({ ...f, age: value as AgeBand }))
              }
              placeholder="Age"
              selectKey={`age-${filters.age}`}
              size="sm"
              value={filters.age}
            />
          </Box>
          <Box className="w-36">
            <DropdownPicker
              items={URBANICITIES}
              onChange={(value) =>
                setFilters((f) => ({
                  ...f,
                  urbanicity: value as Urbanicity,
                }))
              }
              placeholder="Area"
              selectKey={`area-${filters.urbanicity}`}
              size="sm"
              value={filters.urbanicity}
            />
          </Box>
          {filterLabel && (
            <Pressable onPress={() => setFilters(DEFAULT_FILTERS)}>
              <Text className="text-xs font-medium text-primary-600">
                Clear filters
              </Text>
            </Pressable>
          )}
        </HStack>
      </HStack>

      {tab === "questionnaire" ? (
        <SurveyResultsTab
          audience={audience}
          configuration={configuration}
          filters={filters}
        />
      ) : (
        <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
          <VStack className="gap-6 pb-8">
            {tab === "results" ? (
              <HStack className="flex-col lg:flex-row gap-6 items-start">
                <Box className="w-full lg:w-[420px] lg:shrink-0">
                  <ConfigurationPanel
                    audienceLabel={audienceLabel}
                    configuration={configuration}
                    modified={modified}
                    onChange={changeFactor}
                    onReset={() => setEdited(null)}
                  />
                </Box>

                <VStack className="flex-1 w-full gap-6 min-w-0">
                  <InsightCard
                    body={insight.body}
                    headline={insight.headline}
                    sources={insight.sources}
                  />
                  <KpiPanel
                    audience={audience}
                    audienceLabel={
                      filterLabel
                        ? `${audienceLabel} · ${filterLabel}`
                        : audienceLabel
                    }
                    configuration={configuration}
                    kpis={kpis}
                    national={national}
                    onOpenEvidence={() =>
                      setEvidence({ audience, kpi: selectedKpi })
                    }
                    onSelect={setSelectedKpi}
                    optimal={modified ? optimum.kpis : null}
                    sampleSize={sampleSize}
                    selected={selectedKpi}
                  />
                  <MapPanel
                    audienceLabel={audienceLabel}
                    included={included}
                    kpi={selectedKpi}
                    national={kpis}
                    results={regional}
                  />
                </VStack>
              </HStack>
            ) : (
              <FindingsTab
                audience={audience}
                filterLabel={filterLabel}
                filters={filters}
                onOpenEvidence={setEvidence}
              />
            )}

            <Text className="text-xs text-typography-400 text-center">
              {DISCLAIMER}
            </Text>
          </VStack>
        </ScrollView>
      )}

      <ResponsesSheet
        configuration={configuration}
        onClose={() => setEvidence(null)}
        request={evidence}
      />
    </VStack>
  );
};

export default LaunchSimulator;
